import axios from 'axios';
import { GeolocationRequiredError } from './geolocation';
import { humanize } from './stringCase';

interface ValidationDetail {
  msg: string;
  loc?: (string | number)[];
  type?: string;
}

/**
 * Errors raised client-side BEFORE the request leaves, whose message is already
 * written for the user. Without this, `getApiErrorMessage` treated them as
 * "not an axios error" and returned the caller's generic fallback — so a client
 * who denied the browser location prompt was told "Could not save bank account"
 * with no hint that location was the blocker, on three money paths
 * (error audit, 2026-09-11).
 */
function isUserFacingLocalError(err: unknown): err is Error {
  return err instanceof GeolocationRequiredError;
}

/**
 * A vehicle's terms (name, currency, yield, term, fees) are read live from
 * funds (0073) — the portal keeps no copy. When funds cannot answer, a few
 * write paths that need those terms (a proposal, a statement) refuse with
 * `503 { code: 'funds_unavailable' }`, with no `message` field. Handled
 * centrally so every caller of `getApiErrorMessage` gets the same clear
 * copy instead of a generic fallback.
 */
const FUNDS_UNAVAILABLE_MESSAGE =
  'Series terms are temporarily unavailable. Please try again shortly.';

/**
 * FastAPI 422 bodies are an array of `{msg, loc}` where `loc` is the *backend
 * schema path* — `['body', 'beneficiaries', 0, 'rfc']`. Joining that whole path
 * showed an investor our nesting and our array indices. Only the LAST segment
 * survives, humanised: enough to say which field, without the structure.
 */
function fieldLabel(loc: (string | number)[] | undefined): string | null {
  if (!loc) return null;
  const named = loc.filter(
    (segment): segment is string => typeof segment === 'string' && segment !== 'body',
  );
  const last = named[named.length - 1];
  return last ? humanize(last) : null;
}

function formatValidationDetail(detail: ValidationDetail[]): string {
  const parts = detail.map((d) => {
    const msg = (d.msg ?? '').replace(/^Value error,\s*/i, '').trim();
    const label = fieldLabel(d.loc);
    return label ? `${label}: ${msg}` : msg;
  });
  return parts.filter(Boolean).join(', ');
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (isUserFacingLocalError(err)) return err.message;
  if (!axios.isAxiosError(err)) return fallback;

  // No response at all: the request never completed. Distinguishing this from a
  // server rejection matters, because the user's next move is different —
  // retrying a network drop is sensible, retrying a 409 is not.
  if (!err.response) {
    return err.code === 'ECONNABORTED'
      ? 'That took too long to respond. Please check your connection and try again.'
      : 'We could not reach the server. Please check your connection and try again.';
  }

  const detail = err.response.data?.detail;
  if (Array.isArray(detail)) {
    return formatValidationDetail(detail as ValidationDetail[]) || fallback;
  }
  if (typeof detail === 'string') return detail;
  // Structured errors surface as `{ code, message }`.
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    if (detail.code === 'funds_unavailable') return FUNDS_UNAVAILABLE_MESSAGE;
    if (typeof detail.message === 'string') return detail.message;
  }
  return fallback;
}

/**
 * Extract the machine-readable `code` from a structured backend error
 * (`detail: { code, message }`). Returns null for plain-string / validation
 * errors. Used to branch on specific outcomes — e.g. routing an
 * `mfa_not_enrolled` failure to the security-settings enrolment flow.
 */
export function getApiErrorCode(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const detail = err.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)
      && typeof detail.code === 'string') {
    return detail.code;
  }
  return null;
}

/**
 * The support reference carried by a server error, so the user can quote eight
 * characters and someone can find the actual cause in the logs. Null for
 * anything that is not a 5xx from our own API.
 */
export function getApiErrorReference(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const detail = err.response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.reference === 'string') {
    return detail.reference;
  }
  return null;
}

/**
 * Seconds to wait after a 429, from the body or the `Retry-After` header.
 * Returns null when the error is not a rate limit. Before this existed the
 * frontend read neither, so a rate-limited user was shown a generic failure and
 * retried straight back into the limit.
 */
export function getRetryAfterSeconds(err: unknown): number | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 429) return null;
  const detail = err.response.data?.detail;
  const fromBody =
    detail && typeof detail === 'object' ? Number(detail.retry_after) : Number.NaN;
  if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
  const fromHeader = Number(err.response.headers?.['retry-after']);
  return Number.isFinite(fromHeader) && fromHeader > 0 ? Math.ceil(fromHeader) : null;
}
